import { useState, useEffect } from 'react';
import { useApi } from '../api';

interface ExportSummary {
    total_expenses: number;
    total_amount: number;
    legacy_unreimbursed_gross_amount: number;
    by_category: Record<string, { count: number; total: number }>;
    by_year: Record<number, { count: number; total: number }>;
    by_compliance: Record<string, { count: number; total: number }>;
}

export default function Export() {
    const api = useApi();
    const [summary, setSummary] = useState<ExportSummary | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloadingZip, setDownloadingZip] = useState(false);

    // Filter state
    const currentYear = new Date().getFullYear();
    const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
    const [endDate, setEndDate] = useState(`${currentYear}-12-31`);
    const [category, setCategory] = useState('');
    const [compliance, setCompliance] = useState('');

    useEffect(() => {
        loadSummary();
    }, [startDate, endDate, category, compliance]);

    async function loadSummary() {
        setLoading(true);
        try {
            const params: Record<string, string> = {};
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            if (category) params.category = category;
            if (compliance) params.compliance = compliance;

            const result = await api.getExportSummary(params);
            if (result.success && result.data) {
                setSummary(result.data as ExportSummary);
            }
        } catch (error) {
            console.error('Failed to load summary:', error);
        } finally {
            setLoading(false);
        }
    }

    async function handleDownload(format: 'csv' | 'json' | 'zip', fullBackup=false) {
        setDownloadingZip(true);
        try {
            const params: Record<string,string> = {};
            if (!fullBackup) {
                if (startDate) params.start_date = startDate;
                if (endDate) params.end_date = endDate;
                if (category) params.category = category;
                if (compliance) params.compliance = compliance;
            }
            const blob = await api.downloadExport(format,params);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `hsa-expenses-${new Date().toISOString().slice(0,10)}.${format}`;
            link.click();
            setTimeout(() => URL.revokeObjectURL(url),60_000);
        } catch (error) { alert(error instanceof Error ? error.message : 'Export failed'); }
        finally { setDownloadingZip(false); }
    }

    return (
        <div className="export-page">
            <header className="page-header">
                <h1>Export Data</h1>
            </header>

            <div className="export-grid">
                {/* Filters */}
                <div className="card filters-card">
                    <h2>Filter Exports</h2>

                    <div className="form-group">
                        <label htmlFor="start-date">Start Date</label>
                        <input
                            id="start-date"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="end-date">End Date</label>
                        <input
                            id="end-date"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="category">Category</label>
                        <select
                            id="category"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                        >
                            <option value="">All Categories</option>
                            <option value="medical">Medical</option>
                            <option value="dental">Dental</option>
                            <option value="vision">Vision</option>
                            <option value="prescriptions">Prescriptions</option>
                            <option value="equipment">Equipment</option>
                            <option value="otc">OTC</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="compliance">Compliance Status</label>
                        <select
                            id="compliance"
                            value={compliance}
                            onChange={(e) => setCompliance(e.target.value)}
                        >
                            <option value="">All Status</option>
                            <option value="green">🟢 Documents Present</option>
                            <option value="yellow">🟡 Partial</option>
                            <option value="red">🔴 Needs Docs</option>
                        </select>
                    </div>
                </div>

                {/* Summary */}
                <div className="card summary-card">
                    <h2>Export Preview</h2>

                    {loading ? (
                        <div className="loading"><div className="spinner"></div></div>
                    ) : summary ? (
                        <dl className="summary-stats">
                            <dt>Total Expenses</dt>
                            <dd>{summary.total_expenses}</dd>

                            <dt>Total Amount</dt>
                            <dd>${summary.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</dd>

                            <dt>Legacy Unreimbursed Gross</dt>
                            <dd className="highlight">
                                ${summary.legacy_unreimbursed_gross_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </dd>

                            <dt>By Compliance</dt>
                            <dd>
                                <span className="compliance-mini">
                                    🟢 {summary.by_compliance?.green?.count || 0} |
                                    🟡 {summary.by_compliance?.yellow?.count || 0} |
                                    🔴 {summary.by_compliance?.red?.count || 0}
                                </span>
                            </dd>
                        </dl>
                    ) : (
                        <p>No data to export</p>
                    )}
                </div>

                {/* Export Buttons */}
                <div className="card export-actions-card">
                    <h2>Download</h2>
                    <button className="btn btn-secondary" disabled={downloadingZip} onClick={()=>handleDownload('zip',true)}>Download Full Creator Backup</button>
                    <p>The full backup includes all of your entries, payment history and originals, regardless of these report filters.</p>

                    <div className="export-buttons">
                        <button
                            onClick={() => handleDownload('csv')}
                            className="btn btn-primary btn-large"
                        >
                            📄 Download CSV
                            <span className="btn-subtitle">Spreadsheet format</span>
                        </button>

                        <button
                            onClick={() => handleDownload('json')}
                            className="btn btn-secondary btn-large"
                        >
                            📋 Download JSON
                            <span className="btn-subtitle">With document references</span>
                        </button>

                        <button
                            onClick={() => handleDownload('zip')}
                            className="btn btn-success btn-large"
                            disabled={downloadingZip || !summary || summary.total_expenses === 0}
                        >
                            {downloadingZip ? (
                                <>
                                    <span className="spinner-small"></span> Preparing...
                                </>
                            ) : (
                                <>
                                    📦 Download ZIP Package
                                    <span className="btn-subtitle">Originals and metadata (review required)</span>
                                </>
                            )}
                        </button>
                    </div>

                    <div className="export-info">
                        <h3>Included Fields</h3>
                        <ul>
                            <li>Date of Service</li>
                            <li>Provider/Merchant</li>
                            <li>Patient Name</li>
                            <li>Amount</li>
                            <li>Category</li>
                            <li>Family Member</li>
                            <li>Tax Year</li>
                            <li>Legacy eligibility flag (not approval)</li>
                            <li>Reimbursement Status</li>
                            <li>Document Presence (Not Tax Approval)</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}
